import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Flex, Heading, Text, Button, Textarea, VStack, Badge,
  Image, SimpleGrid, Spinner, useToast, Icon, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, Input,
} from "@chakra-ui/react";
import { FaCloudUploadAlt, FaCheckCircle, FaTimesCircle, FaImage, FaFileAlt } from "react-icons/fa";
import axios from "axios";
import { baseUrlApi } from "../config";

const LARANJA = "#F68633";

// Moldura no escopo do módulo (se ficar dentro do componente, cada render recria
// o tipo e remonta a árvore, fazendo os inputs perderem o foco).
const Moldura = ({ children }) => (
  <Flex minH="100vh" bg="gray.50" align="flex-start" justify="center" py={[4, 10]} px={3}>
    <Box w="100%" maxW="560px" bg="white" borderRadius="xl" boxShadow="md" overflow="hidden">
      <Box bg={LARANJA} px={6} py={4}>
        <Heading size="md" color="white">TAGA Autopartes</Heading>
      </Box>
      <Box p={6}>{children}</Box>
    </Box>
  </Flex>
);

// Formulário isolado, com seu PRÓPRIO estado de digitação. Como é um componente
// estável (escopo de módulo), digitar aqui não re-renderiza o componente de
// página (que guarda dados/loading) — o foco do input fica preservado.
function FormularioSolicitacao({ token, dados, toast, onConcluido }) {
  const [arquivos, setArquivos] = useState([]);
  const [resposta, setResposta] = useState("");
  const [observacao, setObservacao] = useState("");
  const [autor, setAutor] = useState("");
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Arquivo e/ou texto. Só com arquivo, quem tinha a resposta escrita (medida,
  // data, nº de fios) usava "Não vai ser possível" e o cliente ouvia que não deu.
  const enviar = async () => {
    if (!arquivos.length && !resposta.trim()) {
      toast({ title: "Anexe um arquivo ou escreva a resposta", status: "warning", duration: 2500, position: "top" });
      return;
    }
    setEnviando(true);
    try {
      const form = new FormData();
      arquivos.forEach((f) => form.append("arquivos[]", f));
      if (resposta.trim()) form.append("resposta", resposta.trim());
      if (observacao) form.append("observacao", observacao);
      if (autor) form.append("autor", autor);
      await axios.post(`${baseUrlApi}/solicitacao-publica/${token}/anexar`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onConcluido("anexado");
    } catch (e) {
      toast({ title: e?.response?.data?.erro || "Falha ao enviar", status: "error", duration: 3500, position: "top" });
    } finally {
      setEnviando(false);
    }
  };

  const enviarRecusa = async () => {
    if (!motivo.trim()) {
      toast({ title: "Explique o motivo", status: "warning", duration: 2500, position: "top" });
      return;
    }
    setEnviando(true);
    try {
      await axios.post(`${baseUrlApi}/solicitacao-publica/${token}/recusar`, { motivo, autor });
      onClose();
      onConcluido("recusado");
    } catch (e) {
      toast({ title: e?.response?.data?.erro || "Falha ao enviar", status: "error", duration: 3500, position: "top" });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <VStack align="stretch" spacing={4}>
        <Box>
          <Badge colorScheme="orange" mb={2}>{dados?.tipo_label}</Badge>
          <Heading size="md">Solicitação de {dados?.tipo_label?.toLowerCase()}</Heading>
        </Box>

        {(dados?.produto_nome || dados?.produto_codigo) && (
          <Box bg="gray.50" borderRadius="md" p={3}>
            <Text fontSize="sm" color="gray.500">Produto</Text>
            <Text fontWeight="600">{dados?.produto_nome || "—"}</Text>
            {dados?.produto_codigo && <Text fontSize="sm" color="gray.600">Código: {dados.produto_codigo}</Text>}
          </Box>
        )}

        <Box>
          <Text fontSize="sm" color="gray.500">O que o cliente pediu</Text>
          <Text>{dados?.descricao}</Text>
        </Box>

        <Divider />

        <Box>
          <Text fontWeight="600" mb={2}>Anexar arquivos</Text>
          <Box
            as="label"
            display="flex" flexDirection="column" alignItems="center" justifyContent="center"
            border="2px dashed" borderColor="gray.300" borderRadius="lg" p={6}
            cursor="pointer" _hover={{ borderColor: LARANJA, bg: "orange.50" }}
          >
            <Icon as={FaCloudUploadAlt} boxSize={8} color={LARANJA} mb={2} />
            <Text color="gray.600" fontSize="sm">Toque para escolher foto, vídeo ou arquivo</Text>
            <Input
              type="file" multiple display="none"
              accept="image/*,video/*,application/pdf"
              onChange={(e) => setArquivos(Array.from(e.target.files || []))}
            />
          </Box>

          {arquivos.length > 0 && (
            <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={2} mt={3}>
              {arquivos.map((f, i) => (
                <Box key={i} borderWidth="1px" borderRadius="md" p={2} textAlign="center">
                  {f.type.startsWith("image") ? (
                    <Image src={URL.createObjectURL(f)} alt={f.name} h="64px" w="100%" objectFit="cover" borderRadius="sm" />
                  ) : (
                    <Flex h="64px" align="center" justify="center" color="gray.400">
                      <Icon as={f.type.startsWith("video") ? FaImage : FaFileAlt} boxSize={6} />
                    </Flex>
                  )}
                  <Text fontSize="xs" noOfLines={1} mt={1}>{f.name}</Text>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </Box>

        <Box>
          <Text fontWeight="600" mb={1}>Resposta por escrito</Text>
          <Text fontSize="sm" color="gray.600" mb={2}>Medida, data, código ou o que foi pedido. Vai para o cliente.</Text>
          <Textarea value={resposta} onChange={(e) => setResposta(e.target.value)} placeholder="Ex.: comprimento 162 cm, 10 fios" rows={3} />
        </Box>

        <Box>
          <Text fontSize="sm" color="gray.600" mb={1}>Observação (opcional)</Text>
          <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Algo que o atendimento deva saber" rows={2} />
        </Box>

        <Box>
          <Text fontSize="sm" color="gray.600" mb={1}>Seu nome (opcional)</Text>
          <Input value={autor} onChange={(e) => setAutor(e.target.value)} placeholder="Quem está respondendo" />
        </Box>

        <Button bg={LARANJA} color="white" _hover={{ bg: "#e07628" }} onClick={enviar} isLoading={enviando} leftIcon={<FaCloudUploadAlt />}>
          Enviar
        </Button>
        <Button variant="ghost" colorScheme="red" onClick={onOpen} isDisabled={enviando}>
          Não vai ser possível
        </Button>
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Não será possível atender?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={2}>Conte rapidamente o motivo (o atendimento vai adaptar para o cliente).</Text>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: peça não disponível para foto no momento" rows={3} />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
            <Button colorScheme="red" onClick={enviarRecusa} isLoading={enviando}>Confirmar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// Página PÚBLICA (sem login): o fornecedor externo abre pelo link com token,
// vê o que foi pedido e anexa a foto/vídeo/arquivo (ou marca que não é possível).
// Usa axios global + rotas públicas (a instância `api` desloga em qualquer 401).
export default function SolicitacaoPublica() {
  const { token } = useParams();
  const toast = useToast();

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [dados, setDados] = useState(null);
  const [concluido, setConcluido] = useState(null); // 'anexado' | 'recusado'

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await axios.get(`${baseUrlApi}/solicitacao-publica/${token}`);
      setDados(res.data);
    } catch (e) {
      setErro(e?.response?.status === 404 ? "Solicitação não encontrada." : "Não foi possível carregar a solicitação.");
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  if (carregando) {
    return <Moldura><Flex justify="center" py={10}><Spinner size="xl" color={LARANJA} /></Flex></Moldura>;
  }
  if (erro) {
    return <Moldura><Text fontSize="lg" color="gray.600" textAlign="center" py={6}>{erro}</Text></Moldura>;
  }

  if (concluido) {
    const ok = concluido === "anexado";
    return (
      <Moldura>
        <VStack spacing={4} py={6}>
          <Icon as={ok ? FaCheckCircle : FaTimesCircle} boxSize={14} color={ok ? "green.400" : "gray.400"} />
          <Heading size="md" textAlign="center">
            {ok ? "Recebido, obrigado!" : "Resposta registrada"}
          </Heading>
          <Text color="gray.600" textAlign="center">
            {ok
              ? "Sua resposta foi enviada. O atendimento vai repassar ao cliente."
              : "Avisamos o atendimento de que não será possível desta vez."}
          </Text>
        </VStack>
      </Moldura>
    );
  }

  if (dados?.finalizada) {
    return (
      <Moldura>
        <VStack spacing={4} py={6}>
          <Icon as={FaCheckCircle} boxSize={12} color="green.400" />
          <Heading size="md" textAlign="center">Esta solicitação já foi atendida</Heading>
          <Text color="gray.600" textAlign="center">Não é necessário fazer mais nada por aqui.</Text>
        </VStack>
      </Moldura>
    );
  }

  return (
    <Moldura>
      <FormularioSolicitacao token={token} dados={dados} toast={toast} onConcluido={setConcluido} />
    </Moldura>
  );
}
